import { useCallback, useEffect, useRef, useState } from 'react';
import { HEADER_HEIGHT } from '@/utils/explorer';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { ApiErrorEnum } from '@/types/api';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import ErrorMessage from './shared/ErrorMessage';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import Feedback from './shared/Feedback';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import DefaultTrainerContent from './content/DefaultTrainerContent';

interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
}

export default function DefaultTrainer({
  data: initialData,
  page,
  name,
  description,
  location,
  tool,
}: Props) {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<any | undefined>(initialData);
  const { leaseId = '' } = data?.content || {};
  const isDataReady = Boolean(data?.content);
  const [isBenchmark, setIsBenchmark] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const { feedback, setFeedback } = useTrainerFeedback();

  const { incrementReviews } = useTrainerReviews();

  const clearAllData = () => {
    setData(undefined);
    setIsBenchmark(false);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };

  const onSubmit = async ({ annotations, outcome, value }: ITrainerSubmit) => {
    clearAllData();

    const sending = {
      leaseId,
      ...(annotations && { annotations }),
      ...(outcome && { outcome }),
      ...(value && { value }),
    };
    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }
    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );
    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };

  useEffect(() => {
    const calculateContentHeight = () => {
      const pageHeaderHeight = HEADER_HEIGHT;
      const sectionHeaderHeight = headerRef.current?.offsetHeight ?? 0;
      const controllerHeight = controllerRef.current?.offsetHeight ?? 0;
      const footerHeight = footerRef.current?.offsetHeight ?? 0;
      const newContentHeight =
        window.innerHeight -
        pageHeaderHeight -
        sectionHeaderHeight -
        controllerHeight -
        footerHeight;
      setContentHeight(newContentHeight);
    };

    calculateContentHeight();
    window.addEventListener('resize', calculateContentHeight);

    return () => window.removeEventListener('resize', calculateContentHeight);
  }, [leaseId]);

  useEffect(() => {
    if (
      data &&
      !data.error &&
      !data?.content?.images?.length &&
      !data?.content?.mapFeatureCandidate
    ) {
      const error = ApiErrorEnum.NoContent;
      setData({ error });
    }
  }, [data]);

  return (
    <div className="flex size-full flex-col items-center justify-start">
      <TrainerHeader
        data={data}
        name={name}
        description={data?.content?.qaToolDescriptionOverride ?? description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <DefaultTrainerContent
            data={data}
            tool={tool}
            contentHeight={contentHeight}
          />
          <TrainerController
            containerRef={controllerRef}
            onSubmit={onSubmit}
            data={data}
            tool={tool}
            isDataReady={isDataReady}>
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
}
